import { useEffect, memo, CSSProperties, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectValidParams, selectGridData, selectDate } from '@pages/reporting-pages/shipment-stops/store/selectors';
import { createGridData } from '@pages/reporting-pages/shipment-stops/store/slice';
import { useLazyShipmentStopsQuery } from '@api/order-reporting/api';
import { GridLoading, GridError, GridEmpty, GridNoParams } from 'components/global/GridElements';
import { useGridSorting } from 'components/filtering/filter-logic/useGridSorting';
import { useGridFiltering } from 'components/filtering/filter-logic/useGridFiltering';
import GridHeader from '@pages/reporting-pages/shipment-stops/grid-data/GridHeader';
import GridRow from '@pages/reporting-pages/shipment-stops/grid-data/GridRow';
import { ShipmentStopsReport } from '@api/order-reporting/types/ShipmentStopsReport';
import { getTimelineLimitTimes, createHoursArray } from '@pages/reporting-pages/shipment-stops/grid-data/utils';
import { headersObject } from '@pages/reporting-pages/shipment-stops/headersObject';



const Grid = () => {

	const validParams = useSelector(selectValidParams);
	const gridData = useSelector(selectGridData);
	const selectedDate = useSelector(selectDate);
	const computedLimitTimes = useMemo(() => getTimelineLimitTimes(gridData), [ gridData ]);
	const hoursArray = useMemo(() => createHoursArray(computedLimitTimes.minArrival, computedLimitTimes.hoursLength, selectedDate), [ computedLimitTimes ]);

	const [ runQuery, { data, isLoading, isFetching, isError, isSuccess } ] = useLazyShipmentStopsQuery();
	const [ filteredData, filterObject, handleSearchValues, handleAddFilterValues, clearFilter ] = useGridFiltering(gridData);
	const [ sortedData, sortObject, handleSorting, clearSorting ] = useGridSorting(filteredData);

	const dispatch = useDispatch();
	const runCreateGridData = (data: ShipmentStopsReport[] | undefined | null) => dispatch(createGridData(data));

	const className = 'app-grid shipment-stops-grid';
	const style: CSSProperties = {
		gridTemplateColumns: `${headersObject.map(x => x.width).join(' ')} 0.5rem auto`,
	}


	useEffect(() => {
		if (validParams) {
			runQuery({ ...validParams }, true);
		}
	}, [ validParams ]);


	useEffect(() => {
		if (data) {
			runCreateGridData(data);
		}
	}, [ data ]);


	if (!validParams) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridNoParams headersLength={headersObject.length} />
		</div>
	);

	if (isLoading || isFetching) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridLoading headersLength={headersObject.length} />
		</div>
	);

	if (isError || !data) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridError headersLength={headersObject.length} />
		</div>
	);
	
	if (isSuccess && gridData && gridData.length === 0) return (
		<div className={className} style={style}>
			<GridHeader />
			<GridEmpty headersLength={headersObject.length} />
		</div>
	);

	return (
		<div className={className} style={style}>
			
			<GridHeader
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
				hoursArray={hoursArray}
			/>
			
			{sortedData && sortedData.map((x,i) =>
				<GridRow
					hoursArray={hoursArray}
					limitTimes={computedLimitTimes}
					key={`id-shipment-stops-grid-${x.rowSystemId ? x.rowSystemId : 'without-id'}`}
					item={x}
					rowIndex={i}
				/>
			)}
		</div>
	)
}

export default memo(Grid);